import React, { useEffect, useState, useCallback } from 'react';
import { Card, Table, Tag, Typography, message, Input, Button, Space } from 'antd';
import { WalletOutlined, SearchOutlined } from '@ant-design/icons';
import api from '../../services/api';

const { Title, Text } = Typography;

interface RechargeOrder {
  id: number;
  order_no: string;
  user_id: number;
  package_id: number;
  payment_method_id: number;
  amount: number;
  bonus_amount: number;
  total_amount: number;
  status: string;
  payment_qr_code: string;
  payment_order_no: string;
  client_ip: string;
  failure_reason: string;
  paid_at: string;
  expired_at: string;
  created_at: string;
  package?: { name: string };
  payment_method?: { name: string };
}

const statusMap: Record<string, { color: string; text: string }> = {
  pending: { color: 'processing', text: '待支付' },
  paid: { color: 'success', text: '已支付' },
  failed: { color: 'error', text: '支付失败' },
  expired: { color: 'default', text: '已过期' },
  cancelled: { color: 'default', text: '已取消' },
};

const RechargeOrderManagement: React.FC = () => {
  const [orders, setOrders] = useState<RechargeOrder[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [searchUserId, setSearchUserId] = useState<string>('');

  const fetchOrders = useCallback(async () => {
    setLoading(true);
    try {
      const params: any = { page, page_size: pageSize };
      if (searchUserId) params.user_id = searchUserId;
      const response = await api.get('/api/admin/v1/recharge/orders', { params });
      if (response.data.code === 200) {
        setOrders(response.data.data.orders || []);
        setTotal(response.data.data.total || 0);
      } else {
        message.error(response.data.message || '获取充值订单失败');
      }
    } catch (error) {
      message.error('获取充值订单失败');
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, searchUserId]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  const columns = [
    {
      title: '订单号',
      dataIndex: 'order_no',
      key: 'order_no',
      render: (text: string) => <Text copyable={{ text }}>{text}</Text>,
    },
    {
      title: '用户ID',
      dataIndex: 'user_id',
      key: 'user_id',
    },
    {
      title: '套餐',
      dataIndex: ['package', 'name'],
      key: 'package',
      render: (_: any, record: RechargeOrder) => record.package?.name || '-',
    },
    {
      title: '充值金额',
      dataIndex: 'amount',
      key: 'amount',
      render: (amount: number) => `¥${amount.toFixed(2)}`,
    },
    {
      title: '赠送金额',
      dataIndex: 'bonus_amount',
      key: 'bonus_amount',
      render: (bonus: number) => bonus > 0 ? `¥${bonus.toFixed(2)}` : '-',
    },
    {
      title: '到账金额',
      dataIndex: 'total_amount',
      key: 'total_amount',
      render: (total: number) => <Text strong>¥{total.toFixed(2)}</Text>,
    },
    {
      title: '支付方式',
      dataIndex: ['payment_method', 'name'],
      key: 'payment_method',
      render: (_: any, record: RechargeOrder) => record.payment_method?.name || '-',
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => {
        const info = statusMap[status] || { color: 'default', text: status };
        return <Tag color={info.color}>{info.text}</Tag>;
      },
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (time: string) => new Date(time).toLocaleString(),
    },
    {
      title: '支付时间',
      dataIndex: 'paid_at',
      key: 'paid_at',
      render: (time: string) => time ? new Date(time).toLocaleString() : '-',
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Title level={2}><WalletOutlined /> 充值订单管理</Title>
      <Card style={{ marginTop: 16 }}>
        <Space style={{ marginBottom: 16 }}>
          <Input
            placeholder="按用户ID筛选"
            value={searchUserId}
            onChange={e => setSearchUserId(e.target.value)}
            style={{ width: 200 }}
            allowClear
          />
          <Button icon={<SearchOutlined />} type="primary" onClick={() => { setPage(1); fetchOrders(); }}>
            查询
          </Button>
        </Space>
        <Table
          columns={columns}
          dataSource={orders}
          rowKey="id"
          loading={loading}
          pagination={{
            current: page,
            pageSize,
            total,
            showSizeChanger: true,
            onChange: (p, ps) => { setPage(p); setPageSize(ps); },
            showTotal: t => `共 ${t} 条`
          }}
          scroll={{ x: 1000 }}
        />
      </Card>
    </div>
  );
};

export default RechargeOrderManagement; 